/**
 * Blog posts. To publish a new post, add a new object to the top of this array.
 *
 * Required fields:
 *   slug          – URL segment, e.g. "choosing-awning-fabric" → /blog/choosing-awning-fabric
 *   title         – Post title (used in <h1> and og:title)
 *   excerpt       – 1–2 sentence summary shown on the index and used as og:description
 *   date          – ISO date "YYYY-MM-DD"
 *   author        – Display name
 *   coverImage    – Imported asset (preferred) or absolute URL string
 *   body          – Array of blocks. Each block is one of:
 *                     { type: "p",  text: "..." }                    paragraph
 *                     { type: "h2", text: "..." }                    section heading
 *                     { type: "ul", items: ["...", "..."] }          bullet list
 *                     { type: "quote", text: "...", who?: "..." }    pull quote
 *                     { type: "img", src: <import>, alt: "..." }     inline image
 *
 * No build step needed — just save and the new post is live at /blog/<slug>.
 */
import heroImg from "@/assets/original/hero-shade.jpg";
import fabImg from "@/assets/marine-canvas-boat.jpg";
import frameImg from "@/assets/original/welder-aluminum.jpg";

export type BlogBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "quote"; text: string; who?: string }
  | { type: "img"; src: string; alt: string };

export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string; // ISO YYYY-MM-DD
  author: string;
  coverImage: string;
  /** Fabric/category tags, e.g. ["awning", "marine", "upholstery"]. */
  tags?: string[];
  body: BlogBlock[];
}

export const posts: BlogPost[] = [
  {
    slug: "why-you-should-never-attach-a-sail-shade-to-your-home",
    title: "Why You Should Never Attach a Sail Shade Directly to Your Home",
    excerpt:
      "That cheap DIY sail shade kit might cost you far more than the price tag. Here's why attaching shade sails to your house is a risk to your structure — and your insurance coverage.",
    date: "2026-06-18",
    author: "Texas Made Shade & Covers",
    coverImage: heroImg,
    tags: ["awning", "sail-shade"],
    body: [
      {
        type: "p",
        text:
          "Sail shades look great in the catalog. A triangle of fabric, a few attachment points, and instant shade over your patio. But when that attachment point is your house — your roof line, fascia board, or exterior wall — you're gambling with your biggest investment.",
      },
      { type: "h2", text: "The wind load you don't see coming" },
      {
        type: "p",
        text:
          "A sail shade catches wind like a parachute. In a 30-mph gust, that triangle of fabric can generate hundreds of pounds of pulling force on a single anchor point. When that anchor is your fascia board or roof edge, something has to give. We've been called out to jobs where a sail shade ripped a gutter clean off, pulled siding away from the sheathing, or cracked a fascia board so water now leaks behind the wall.",
      },
      {
        type: "p",
        text:
          "Texas storms don't announce themselves. A calm morning can turn into 50-mph straight-line winds by afternoon. A sail shade bolted to your house has no give — it transfers every bit of that force straight into your structure.",
      },
      { type: "h2", text: "Why your insurance company may say no" },
      {
        type: "p",
        text:
          "Homeowners insurance covers sudden, accidental damage from acts of nature — wind, hail, lightning. But there's a catch: the damage has to be to a properly maintained structure. If an adjuster determines the sail shade was an improper modification that created a new point of failure, your claim can be denied or reduced.",
      },
      { type: "ul", items: [
        "If the sail shade tore off and damaged your roof, the insurer may argue the sail shade caused the damage, not the storm.",
        "If water intrusion happens because a sail shade pulled loose flashing or fascia, that's often classified as long-term neglect or improper installation.",
        "Some policies explicitly exclude damage from 'structures not professionally installed' — and a DIY kit from the big-box store rarely qualifies.",
      ]},
      {
        type: "p",
        text:
          "We've heard from homeowners who paid thousands out of pocket because their sail shade turned a $2,000 wind claim into a $15,000 structural repair the insurer wouldn't touch.",
      },
      { type: "h2", text: "What insurance adjusters actually look for" },
      {
        type: "p",
        text:
          "After a storm, adjusters photograph attachment points. They're looking for evidence of amateur installation: lag bolts into fascia instead of rafters, no backing plate, no structural engineering, no permit. If they find it, they document it. That documentation goes into the claim file and can follow your property record.",
      },
      {
        type: "p",
        text:
          "Even if the claim is partially paid, your policy may be non-renewed. Carriers are increasingly dropping homes with unpermitted shade structures, especially in high-wind counties across South and Central Texas.",
      },
      { type: "h2", text: "The right way to shade your patio" },
      {
        type: "p",
        text:
          "Freestanding shade structures are the answer. A welded aluminum frame — planted in concrete, engineered for your local wind zone, and not touching your house — gives you all the shade with none of the structural risk. If a storm hits, the frame is designed to flex or the fabric is designed to release. Your roof line stays intact.",
      },
      { type: "ul", items: [
        "Freestanding frames transfer wind load to the ground, not your walls.",
        "Professional installation includes proper permits and wind-load calculations.",
        "Insurance companies recognize professionally installed shade structures as legitimate property improvements.",
        "If damage ever does occur, the chain of liability is clear.",
      ]},
      {
        type: "p",
        text:
          "We've built freestanding sail shades, pergola covers, and awnings for Texas homeowners for over 30 years. Every frame is welded in our Seguin shop, engineered for the county it's going to, and installed by our crew — not a subcontractor. When a storm comes, our customers know exactly who to call.",
      },
      {
        type: "quote",
        text: "A sail shade attached to your house saves you $200 today and costs you $20,000 when the wind picks up. Build it right, build it freestanding, and sleep through the storm.",
        who: "TMSC shop floor",
      },
    ],
  },
  {
    slug: "sunbrella-fabric-care-and-buying-guide",
    title: "Sunbrella® Fabric: A Texas Shop's Care & Buying Guide",
    excerpt:
      "Everything we tell customers about Sunbrella — how it's made, why it lasts, how to clean it, and how to order the right fabric by the yard.",
    date: "2026-05-18",
    author: "Texas Made Shade & Covers",
    coverImage: fabImg,
    tags: ["awning", "marine", "upholstery"],
    body: [
      {
        type: "p",
        text:
          "Sunbrella® is the fabric we reach for on almost every awning, boat top, and outdoor cushion that leaves our Seguin shop. After 30+ years of working with it, here's the plain-English guide we wish every customer had before they ordered.",
      },
      { type: "h2", text: "What makes Sunbrella different" },
      {
        type: "p",
        text:
          "Sunbrella is a solution-dyed acrylic — color is locked into the fiber before it's woven, not printed or dyed on the surface. That's why a 10-year-old Sunbrella awning in the Texas sun still has color, while a cheap printed canvas of the same age looks chalky and gray.",
      },
      { type: "ul", items: [
        "Solution-dyed acrylic fiber (color goes all the way through)",
        "5-year warranty on most marine/upholstery, 10-year on Awning & Marine grade",
        "Mold and mildew resistant — they grow on dirt, not on the fiber itself",
        "Bleach-cleanable without losing color",
      ]},
      { type: "h2", text: "Stain resistance & everyday living" },
      {
        type: "p",
        text:
          "Sunbrella fabrics are engineered for everyday spills — sunscreen, BBQ sauce, red wine, kid juice. Most stains lift with mild soap and lukewarm water if you get to them before they set. For tougher stains, a diluted bleach solution is safe on Sunbrella (always rinse thoroughly).",
      },
      { type: "h2", text: "Fade resistance in Texas sun" },
      {
        type: "p",
        text:
          "Because the color is solution-dyed, Sunbrella holds up in direct sun far better than printed fabrics. That said, darker colors (blues, blacks, dark greens) still outlast warm tones (reds, yellows, terra cotta) in our climate. UV breaks down warm-tone pigments faster regardless of fabric quality.",
      },
      { type: "h2", text: "Cleaning & care" },
      { type: "ul", items: [
        "General cleaning: mild soap (Dawn or Woolite), lukewarm water, soft brush, rinse thoroughly",
        "Mold / mildew: 1 cup bleach + 1/4 cup mild soap per gallon of water, rinse well, air dry",
        "Tough stains: check the Sunbrella stain chart — different stains need different cleaners",
        "Never dry-clean or machine dry on high heat — air dry whenever possible",
        "Re-treat with 303 Fabric Guard every 2-3 years to restore water repellency",
      ]},
      { type: "h2", text: "Can I use a fabric protector on Sunbrella?" },
      {
        type: "p",
        text:
          "Yes — and we recommend 303 High Tech Fabric Guard specifically. It's the only protector Sunbrella endorses. Apply it after any deep cleaning and every 2-3 years to keep water beading on the surface instead of soaking in.",
      },
      { type: "h2", text: "How to fix a tear" },
      {
        type: "p",
        text:
          "Small tears (under 3 inches) can be patched with Sunbrella seam sealant tape or sewn on a heavy-duty machine with V-92 polyester thread. For anything larger — or any tear on a structural seam — bring it in. We patch and re-sew Sunbrella every week and a clean professional repair is almost invisible.",
      },
      { type: "h2", text: "Ordering Sunbrella by the yard" },
      {
        type: "p",
        text:
          "We stock the most popular Awning & Marine, Upholstery, and Shade collection colors in our Seguin shop and can order any Sunbrella pattern you find on their site. Standard widths are 46 inches (upholstery) and 60 inches (awning/marine). If you're not sure which collection you need, bring us a swatch or a photo of where it's going and we'll match it.",
      },
      {
        type: "p",
        text:
          "You can browse our full Sunbrella showroom on the Fabrics page, or come by the shop in Seguin to feel the actual material — color on a screen never matches what shows up on your patio.",
      },
      {
        type: "quote",
        text: "Buy the fabric once. Sunbrella over a welded aluminum frame is the longest-lasting awning we know how to build.",
        who: "TMSC shop floor",
      },
    ],
  },
  {
    slug: "choosing-awning-fabric-for-texas-sun",
    title: "Choosing Awning Fabric That Holds Up to the Texas Sun",
    excerpt:
      "Darker colors, marine-grade thread, and the right manufacturer warranty can add years to your awning's life. Here's how we pick fabric for Texas heat.",
    date: "2026-04-22",
    author: "Texas Made Shade & Covers",
    coverImage: fabImg,
    tags: ["awning"],
    body: [
      {
        type: "p",
        text:
          "Fabric is the part of an awning that fights the sun every single day. In Texas, the wrong color or weave will fade and weaken years before the frame ever needs attention.",
      },
      { type: "h2", text: "Color matters more than you think" },
      {
        type: "p",
        text:
          "Darker colors like blue, black, and dark green tend to look better longer than red, yellow, or terra cotta. UV breaks down warm-tone pigments faster, so reds and yellows are usually the first to look chalky.",
      },
      { type: "h2", text: "Look for a real manufacturer warranty" },
      {
        type: "p",
        text:
          "Some manufacturers like Glen Raven (Sunbrella) offer a 10-year limited warranty on their solution-dyed acrylic. Always check who your manufacturer is — generic 'marine canvas' often has no warranty at all.",
      },
      { type: "h2", text: "Don't forget the frame" },
      {
        type: "p",
        text:
          "Even the best fabric is only as good as what it stretches over. Our welded aluminum frames will outlast steel frames by far — no rust, no pipe joints to loosen.",
      },
    ],
  },
  {
    slug: "welded-aluminum-vs-steel-frames",
    title: "Welded Aluminum vs. Steel Awning Frames: What Lasts in Texas?",
    excerpt:
      "Why we build every awning, sail shade, and cover frame from welded aluminum instead of bolted steel pipe.",
    date: "2026-03-10",
    author: "Texas Made Shade & Covers",
    coverImage: frameImg,
    tags: ["awning"],
    body: [
      {
        type: "p",
        text:
          "We get this question on almost every quote: 'Is aluminum really stronger than steel?' For an awning frame in Texas, the answer is yes — and here's why.",
      },
      { type: "h2", text: "Rust is the real enemy" },
      {
        type: "p",
        text:
          "Galvanized or painted steel pipe will eventually corrode where the coating is scratched, drilled, or weathered. Aluminum doesn't rust, period.",
      },
      { type: "h2", text: "Welded beats bolted" },
      {
        type: "ul",
        items: [
          "No fasteners that loosen in Texas wind",
          "Cleaner sightlines — no pipe-clamp hardware showing",
          "Custom angles so the canvas pulls perfectly tight",
        ],
      },
      {
        type: "quote",
        text: "Built once, built right. We'd rather weld it than bolt it.",
        who: "TMSC shop floor",
      },
    ],
  },
  {
    slug: "shade-pro-history",
    title: "30+ Years of Texas Made Shade — Our Story",
    excerpt:
      "A short history of our Seguin shop, the customers we serve, and the standards that have stayed the same for three decades.",
    date: "2026-02-01",
    author: "Texas Made Shade & Covers",
    coverImage: heroImg,
    body: [
      {
        type: "p",
        text:
          "For 30+ years our crew has been patterning, sewing, and welding custom shade in Seguin, Texas. The shop name has evolved but the people, the patterns, and the standards haven't.",
      },
      { type: "h2", text: "Same hands, same standards" },
      {
        type: "p",
        text:
          "We still pattern in-house, still weld our own frames, and still answer the phone when you call. That's the part of the business that was never going to change.",
      },
    ],
  },
];

export function getPost(slug: string): BlogPost | undefined {
  return posts.find((p) => p.slug === slug);
}

export function listPosts(): BlogPost[] {
  return [...posts].sort((a, b) => (a.date < b.date ? 1 : -1));
}
